import { ChangeEvent, useCallback, useEffect, useState, useRef } from "react";
import { TQC2050_R01HeaderDto } from "./Dto_TQC2050_R01";

import { DropDownForm } from "src/components/common/form/DropdownForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { DateTimeForm } from "src/components/common/form/DateTimeForm";
import { BRDiv } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { CodeForm } from "src/components/common/form/CodeForm";
import { getItemPopupList } from "src/apis/itemApi";
import { getEmployeeList } from "src/apis/employeeApi";
import { searchCustomer } from "src/apis/customerApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";

import { useTQC2050_R01Store } from "./Store_TQC2050_R01";
import { defaultTQC2050_R01HeaderDto, defaultTQC2050_R01DetailDto, TQC2050_R01DetailDto } from "./Dto_TQC2050_R01";
import { getMST4010_R01List } from "../../MST/MST4010_R01/Api_MST4010_R01";
import { MST4010_R01Dto } from "../../MST/MST4010_R01/Dto_MST4010_R01";
import { showSuccess, showWarning } from "src/utils/toast/toastUtil";

interface Props {
	data?: TQC2050_R01HeaderDto;
	defaultTQC2050_R01HeaderRow: TQC2050_R01HeaderDto;
}

export const Info_TQC2050_R01 = ({ data, defaultTQC2050_R01HeaderRow }: Props) => {

	const { 
		selectedTQC2050_R01, 
		editTQC2050_R01, 
		setEditTQC2050_R01,
		setTQC2050_R01DetailList,
		setOriginalTQC2050_R01DetailList
	} = useTQC2050_R01Store();
    const { compCdList, carModelList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();
    
    // TXT 파일 업로드용 ref
    const txtFileInputRef = useRef<HTMLInputElement>(null);

    const handleEmpFoucsId = 'empNo_search';
    const handleCustomFoucsId = 'customCd_search';
	const handleItemFoucsId = 'itemNo_search';

    // Info 폼 코드 검색 필드 설정
    const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
        {
            fieldName: 'itemNo',
            category: "품목",
            focusId: handleItemFoucsId,
            responseMapping: {
                itemCd: 'itemNo',  // ✅ API의 itemCd를 DTO의 itemNo로 매핑
                itemNm: 'itemNm',
                modelNo: 'modelNo'
            },
            searchFunction: async (code: string) => {
                const itemResponse = await getItemPopupList(code, '', '', '');
                return itemResponse.data.data.find(value => value.itemCd === code);
            }
        },
        {
            fieldName: 'empNo',
            category: '시험자',
            focusId: handleEmpFoucsId,
            responseMapping: {
                empNo: 'empNo',
                empNm: 'empNm'
            },
            searchFunction: async (code: string) => {
                const employeeResponse = await getEmployeeList('all');
                return employeeResponse.data.data.find(value => value.empNo === code);
            }
        },
        {
            fieldName: 'customCd',
            category: '거래처',
            focusId: handleCustomFoucsId,
            responseMapping: {
                customCd: 'customCd',
                customNm: 'customNm'
            },
            searchFunction: async (searchText: string) => {
                const customerResponse = await searchCustomer(searchText);
                return customerResponse.data.data.find(value => value.customCd === searchText);
            }
        }
    ];

    // Info 폼 이벤트 훅 사용
    const { getFieldHandlers: originalGetFieldHandlers } = useInfoFormEvents({
        editData: editTQC2050_R01,
        setEditData: setEditTQC2050_R01,
        defaultData: defaultTQC2050_R01HeaderRow,
        infoFieldConfigs,
        setCodeSearchModalProps,
        formName: 'TQC2050_R01'
    });

	// getFieldHandlers를 래핑하여 값 변경 시 nm 초기화 로직 추가
	const getFieldHandlers = useCallback((fieldName: string) => {
		const originalHandlers = originalGetFieldHandlers(fieldName);
		
		// 커스텀 onChange 핸들러 생성
		const customOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			if (!editTQC2050_R01) return;
			
			// 기본 핸들러 호출
			originalHandlers.onChange(e);
		};
		
		return {
			...originalHandlers,
			onChange: customOnChange
		};
	}, [originalGetFieldHandlers, editTQC2050_R01, setEditTQC2050_R01, defaultTQC2050_R01HeaderRow]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		if (!editTQC2050_R01) return;

		if (name === 'sampleCount') {
			// 시료수는 숫자로 변환하여 저장
			const numValue = Number(value) || 0;
			setEditTQC2050_R01({ ...editTQC2050_R01, [name]: numValue });
		} else {
			setEditTQC2050_R01({ ...editTQC2050_R01, [name]: value });
		}
	}, [editTQC2050_R01, setEditTQC2050_R01, ]);

	// info 초기값 설정
	useEffect(() => {
        if(editTQC2050_R01) {
            if(!editTQC2050_R01.compCd && compCdList.length > 0) editTQC2050_R01.compCd = compCdList[0].compCd;
        }
    }, [compCdList])

	// 조회 시 itemNo만 있고 modelNo가 비어있다면 자동으로 modelNo 채우기
	useEffect(() => {
		if (!editTQC2050_R01) return;
		const itemNo = editTQC2050_R01.itemNo?.trim();
		const modelNo = editTQC2050_R01.modelNo?.trim();
		if (!itemNo || modelNo) return;

		(async () => {
			try {
				const res = await getItemPopupList(itemNo, '', '', '');
				const found = res.data.data.find((v: any) => v.itemCd === itemNo);
				if (found?.modelNo) {
					setEditTQC2050_R01({ ...editTQC2050_R01, modelNo: found.modelNo });
				}
			} catch (err) {
				// 조회 실패는 무시 (자동 보조 기능)
			}
		})();
	}, [editTQC2050_R01?.itemNo, editTQC2050_R01?.modelNo, setEditTQC2050_R01]);

	// 조회 시 customCd만 있고 customNm이 비어있다면 자동으로 customNm 채우기
	useEffect(() => {
		if (!editTQC2050_R01) return;
		const customCd = editTQC2050_R01.customCd?.trim();
		const customNm = editTQC2050_R01.customNm?.trim();
		if (!customCd || customNm) return;

		(async () => {
			try {
				const res = await searchCustomer(customCd);
				const found = res.data.data?.find((v: any) => v.customCd === customCd);
				if (found?.customNm) {
					setEditTQC2050_R01({ ...editTQC2050_R01, customNm: found.customNm });
				}
			} catch (err) {
				// 조회 실패는 무시 (자동 보조 기능)
			}
		})();
	}, [editTQC2050_R01?.customCd, editTQC2050_R01?.customNm, setEditTQC2050_R01]);

	// TXT 파일 파싱 함수
	const parseTxtFile = useCallback((text: string) => {
		const lines = text.split(/\r?\n/).map(line => line.trim());
		
		// 도금원소 추출 (첫 줄 또는 "방사법" 포함된 줄에서 앞 단어)
		let resultValueParsed = '';
		const platingLine = lines.find(line => line.includes('방사법') || line.includes('측정'));
		if (platingLine) {
			resultValueParsed = platingLine.split(/\s+/)[0]?.trim() || '';
		} else if (lines.length > 0 && lines[0]) {
			resultValueParsed = lines[0].split(/\s+/)[0]?.trim() || '';
		}
		
		// 고객명 추출: "고객명 : (주)금경"
		const customNmMatch = text.match(/고객명\s*:\s*(.+?)(?:\s|$)/);
		const customNm = customNmMatch?.[1]?.trim() || '';
		
		// 품번 추출: "품   번 : 22135-2M800" (공백이 많을 수 있음)
		const itemNoMatch = text.match(/품\s*번\s*:\s*(.+?)(?:\s{2,}|$)/);
		const itemNo = itemNoMatch?.[1]?.trim() || '';
		
		// LOT NO 추출: "LOT NO : 20251103"
		const lotNoMatch = text.match(/LOT\s*NO\s*:\s*(.+?)(?:\s|$)/i);
		const lotNo = lotNoMatch?.[1]?.trim() || '';
		
		// 테이블 데이터 추출 (No, Zn μm, Date & Time, Comment)
		const tableStartIndex = lines.findIndex(line => 
			line.includes('No') && (line.includes('Zn') || line.includes('μm'))
		);

		const detailData: { testValue: string }[] = [];

		if (tableStartIndex >= 0) {
			for (let i = tableStartIndex + 1; i < lines.length; i++) {
				const line = lines[i];
				if (!line || line.includes('최대값') || line.includes('최소값') || line.includes('범위') || 
				line.includes('평균값') || line.includes('표준편차') || line.includes('변동계수')) {
					break; // 통계 데이터 시작 전까지
				}
				
				// 숫자로 시작하는 행만 파싱 (1	7.73	2025-11-03...)
				// 탭 또는 공백으로 구분
				const parts = line.split(/\s+/).filter(part => part.trim());
				if (parts.length >= 2 && /^\d+$/.test(parts[0])) {
					// 첫 번째는 번호, 두 번째는 Zn μm 값
					const testValue = parts[1];
					if (testValue && !isNaN(parseFloat(testValue))) {
						detailData.push({ testValue });
					}
				}
			}
		}
		
		return {
			resultValue: resultValueParsed,
			customNm,
			itemNo,
			lotNo,
			sampleCount: detailData.length,
			detailList: detailData
		};
	}, []);

	// TXT 파일 업로드 핸들러
	const handleTxtFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		
		// 파일 확장자 확인
		if (!file.name.toLowerCase().endsWith('.txt')) {
			showWarning('TXT 파일만 업로드 가능합니다.');
			if (txtFileInputRef.current) txtFileInputRef.current.value = '';
			return;
		}
		
		const reader = new FileReader();
		reader.onload = async (event) => {
			try {
				const text = event.target?.result as string;
				const parsedData = parseTxtFile(text);
				
				if (parsedData.sampleCount === 0) {
					showWarning('파일에서 측정 데이터를 찾을 수 없습니다.');
					if (txtFileInputRef.current) txtFileInputRef.current.value = '';
					return;
				}
				
				// 고객사명으로 customCd 찾기
				let customCd = '';
				if (parsedData.customNm) {
					try {
						const customerResponse = await searchCustomer(parsedData.customNm);
						const foundCustomer = customerResponse.data.data.find(
							customer => customer.customNm === parsedData.customNm
						);
						if (foundCustomer) {
							customCd = foundCustomer.customCd;
						}
					} catch (error) {
						console.warn('고객사 검색 실패:', error);
					}
				}
				
				// 품번으로 itemNo 찾기
				let itemNm = '';
				if (parsedData.itemNo) {
					try {
						const itemResponse = await getItemPopupList(parsedData.itemNo, '', '', '');
						const foundItem = itemResponse.data.data.find(
							item => item.itemCd === parsedData.itemNo
						);
						if (foundItem) {
							itemNm = foundItem.itemNm || '';
						}
					} catch (error) {
						console.warn('품목 검색 실패:', error);
					}
				}
				
				// Info 폼 업데이트
				setEditTQC2050_R01({
					...defaultTQC2050_R01HeaderRow,
					...editTQC2050_R01,
					customCd: customCd,
					customNm: parsedData.customNm,
					itemNo: parsedData.itemNo,
					itemNm: itemNm,
					lotNo: parsedData.lotNo,
					resultValue: parsedData.resultValue,
					sampleCount: parsedData.sampleCount,
				});
				
				// DetailTable 업데이트
				const detailList: TQC2050_R01DetailDto[] = parsedData.detailList.map((item, index) => ({
					...defaultTQC2050_R01DetailDto,
					valueIndex: index + 1,
					orderIndex: index + 1,
					testValue: item.testValue,
					inserted: true,
					isModified: false,
				}));
				
				setTQC2050_R01DetailList(detailList);
				setOriginalTQC2050_R01DetailList(detailList);
				
				showSuccess(`TXT 파일이 성공적으로 업로드되었습니다. (${parsedData.sampleCount}건)`);
			} catch (error) {
				console.error('파일 파싱 오류:', error);
				showWarning('파일을 읽는 중 오류가 발생했습니다.');
			} finally {
				// 파일 input 초기화
				if (txtFileInputRef.current) txtFileInputRef.current.value = '';
			}
		};
		
		reader.onerror = () => {
			showWarning('파일을 읽는 중 오류가 발생했습니다.');
			if (txtFileInputRef.current) txtFileInputRef.current.value = '';
		};
		
		reader.readAsText(file, 'UTF-8');
	}, [editTQC2050_R01, setEditTQC2050_R01, parseTxtFile, setTQC2050_R01DetailList, setOriginalTQC2050_R01DetailList]);

	// 파일 업로드 버튼 클릭 핸들러
	const handleFileUploadClick = useCallback(() => {
		txtFileInputRef.current?.click();
	}, []);

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300">
			<InputForm 
				title="시험번호" 
				value={editTQC2050_R01?.testResultNo || ''} 
				editable={false} 
				name="testResultNo" 
				onChange={handleChange} 
			/>
			{/* TXT 파일 업로드 버튼 (인라인 배치) */}
			<div className="ml-auto mb-2">
				<button
					type="button"
					onClick={handleFileUploadClick}
					className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-1 rounded-md text-sm font-semibold"
				>
					TXT파일 업로드
				</button>
				<input
					type="file"
					accept=".txt"
					ref={txtFileInputRef}
					onChange={handleTxtFileChange}
					style={{ display: 'none' }}
				/>
			</div>
			
			<BRDiv />
			<InputForm 
				title="측정명" 
				value={editTQC2050_R01?.testNm || ''} 
				editable={true} 
				name="testNm" 
				onChange={handleChange} 
			/>
			<CodeForm
				label="측정자"
				value={editTQC2050_R01?.empNo || ""}
				custNmValue={editTQC2050_R01?.empNm || ""}
				editable={true}
				name="empNo"
				handleFoucsId={handleEmpFoucsId}
				handleChange={getFieldHandlers('empNo').onChange}
				onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
				onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
			/>
			<BRDiv />
			<CodeForm
				label="고객사"
				value={editTQC2050_R01?.customCd || ""}
				custNmValue={editTQC2050_R01?.customNm || ""}
				editable={true}
				name="customCd"
				handleFoucsId={handleCustomFoucsId}
				handleChange={getFieldHandlers('customCd').onChange}
				onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
				onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
			/>
			<BRDiv />
			<CodeForm
				label="품목코드"
				value={editTQC2050_R01?.itemNo || ""}
				custNmValue={editTQC2050_R01?.itemNm || ""}
				editable={true}
				name="itemNo"
				handleFoucsId={handleItemFoucsId}
				handleChange={getFieldHandlers('itemNo').onChange}
				onDoubleClick={getFieldHandlers('itemNo').onDoubleClick}
				onKeyDown={getFieldHandlers('itemNo').onEnterKeyDown}
			/>
            <InputForm 
                title="품번" 
                value={editTQC2050_R01?.modelNo || ''} 
                editable={true} 
                name="modelNo" 
                onChange={handleChange} 
            />
			
			<BRDiv />
			<InputForm 
				title="LOT No." 
				value={editTQC2050_R01?.lotNo || ''} 
				editable={true} 
				name="lotNo" 
				onChange={handleChange} 
			/>
			<BRDiv />
			<InputForm title="도금원소" value={editTQC2050_R01?.resultValue || ''} editable={true} name="resultValue" onChange={handleChange} />
			<InputForm 
				title="시료수" 
				value={editTQC2050_R01?.sampleCount || 0} 
				editable={true} 
				name="sampleCount" 
				type="string"
				onChange={handleChange} 
			/>
		</div>
	)
}

